import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { KeyRound, Save, Globe, Bell, Upload, Trash2, Volume2 } from "lucide-react";

export default function SuperAdminSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [platform, setPlatform] = useState(null);          // {settings, sound_options, upload_limits}
  const [platformSaving, setPlatformSaving] = useState(false);
  const [uploadingSound, setUploadingSound] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const [s, p] = await Promise.all([
        api.get("/superadmin/settings"),
        api.get("/superadmin/platform/settings"),
      ]);
      setForm(s.data);
      setPlatform(p.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load settings");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/superadmin/settings", form);
      setForm(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const savePlatform = async (next) => {
    setPlatformSaving(true);
    try {
      const { data } = await api.put("/superadmin/platform/settings", next);
      setPlatform((p) => ({ ...(p || {}), settings: data.settings }));
      toast.success("Push notification settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setPlatformSaving(false); }
  };

  const uploadSound = async (file) => {
    if (!file) return;
    const limits = platform?.upload_limits || { max_bytes: 1048576, allowed_extensions: ["mp3", "wav"] };
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!limits.allowed_extensions.includes(ext)) {
      toast.error(`Unsupported file type ".${ext}". Allowed: ${limits.allowed_extensions.join(", ")}`);
      return;
    }
    if (file.size > limits.max_bytes) {
      toast.error(`File too large (${(file.size / 1024).toFixed(0)} KB). Max ${(limits.max_bytes / 1024).toFixed(0)} KB.`);
      return;
    }
    setUploadingSound(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/superadmin/platform/settings/notification-sound", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPlatform((p) => ({ ...(p || {}), settings: data.settings }));
      toast.success(`Custom notification sound uploaded: ${data.uploaded.filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingSound(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeSound = async () => {
    if (!window.confirm("Remove custom notification sound and revert to the system default?")) return;
    setUploadingSound(true);
    try {
      const { data } = await api.delete("/superadmin/platform/settings/notification-sound");
      setPlatform((p) => ({ ...(p || {}), settings: data.settings }));
      toast.success("Reverted to system default sound");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove sound");
    } finally { setUploadingSound(false); }
  };

  if (!form) {
    return <div className="space-y-4" data-testid="super-settings-loading">
      <div className="h-8 w-48 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
      <div className="h-56 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
    </div>;
  }

  const ps = platform?.settings || {};
  const hasCustomSound = Boolean(ps.notification_sound_url);
  const maxKB = Math.round((platform?.upload_limits?.max_bytes || 1048576) / 1024);
  const allowed = platform?.upload_limits?.allowed_extensions || ["mp3", "wav"];

  return (
    <div className="space-y-6 max-w-3xl" data-testid="super-settings-page">
      <div>
        <h1 className="font-display text-3xl">Platform Settings</h1>
        <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">Billing-wide configuration: payment gateway keys, downgrade policy, default plan.</p>
      </div>

      <section className="bg-white border border-[hsl(var(--kb-border))] p-6 space-y-4" data-testid="platform-domain-section">
        <h2 className="font-display text-xl flex items-center gap-2"><Globe size={18} /> Platform base domain</h2>
        <p className="text-xs text-[hsl(var(--kb-muted))]">
          The apex domain every tenant&apos;s subdomain points to. Example: setting <code>getownapp.com</code> turns every
          tenant&apos;s storefront into <code>{"{slug}"}.getownapp.com</code> (assuming wildcard DNS + SSL are in place).
          Leave empty to keep using the current request host.
        </p>
        <div>
          <label className="text-sm font-medium">Platform base domain</label>
          <input
            className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1 font-mono"
            placeholder="e.g. getownapp.com"
            value={form.platform_base_domain || ""}
            onChange={(e) => setForm({ ...form, platform_base_domain: e.target.value.trim().toLowerCase() })}
            data-testid="platform-base-domain"
          />
        </div>
        <div>
          <label className="text-sm font-medium">Public CNAME target (for custom-domain tenants)</label>
          <input
            className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1 font-mono"
            placeholder="e.g. ingress.getownapp.com"
            value={form.public_cname_target || ""}
            onChange={(e) => setForm({ ...form, public_cname_target: e.target.value.trim().toLowerCase() })}
            data-testid="public-cname-target"
          />
          <p className="text-[10px] text-[hsl(var(--kb-muted))] mt-1">
            Tenants who connect their own domain will be asked to point a CNAME here. Used for DNS verification on /admin/domain.
          </p>
        </div>
        <div>
          <label className="text-sm font-medium">Storefront URL layout</label>
          <select
            className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1 font-mono"
            value={form.platform_url_style || "path"}
            onChange={(e) => setForm({ ...form, platform_url_style: e.target.value })}
            data-testid="platform-url-style"
          >
            <option value="path">Path — https://&lt;base&gt;/t/&lt;slug&gt;/  (works on any host)</option>
            <option value="subdomain">Subdomain — https://&lt;slug&gt;.&lt;base&gt;/  (needs wildcard DNS + SSL)</option>
          </select>
          <p className="text-[10px] text-[hsl(var(--kb-muted))] mt-1">
            Controls how non-default tenant URLs are formatted in Super Admin and emitted by
            the backend. Switching to <b>Subdomain</b> requires a wildcard DNS record
            (<code className="font-mono">*.&lt;base&gt;</code>) and matching wildcard TLS cert
            on your reverse proxy. Existing custom-domain tenants are unaffected.
          </p>
        </div>
        <div
          className="border border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] p-3"
          data-testid="reels-moved-notice"
        >
          <span className="text-sm font-medium block">Reels / short-video content</span>
          <span className="text-[10px] text-[hsl(var(--kb-muted))] block mt-1">
            Reels availability is set <b>per tenant</b>, not platform-wide — one
            tenant&apos;s App Store Guideline 5.2.3 posture must never change another
            tenant&apos;s storefront. Manage it in{" "}
            <Link
              to="/superadmin/tenants"
              className="underline text-[hsl(var(--kb-primary))]"
              data-testid="reels-moved-link"
            >
              Tenants
            </Link>{" "}
            → open a tenant → <b>Reels / short-video content</b>.
          </span>
        </div>
      </section>

      <section className="bg-white border border-[hsl(var(--kb-border))] p-6 space-y-4">
        <h2 className="font-display text-xl flex items-center gap-2"><KeyRound size={18} /> Razorpay (mocked for now)</h2>
        <p className="text-xs text-[hsl(var(--kb-muted))]">These keys are stored but the billing system is currently mocked. Once real charges go live, these will be used as the Razorpay API credentials.</p>
        <div>
          <label className="text-sm font-medium">Key ID</label>
          <input className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1 font-mono"
            placeholder="rzp_live_..."
            value={form.razorpay_key_id || ""}
            onChange={(e) => setForm({ ...form, razorpay_key_id: e.target.value })}
            data-testid="razorpay-key-id" />
        </div>
        <div>
          <label className="text-sm font-medium">Key Secret</label>
          <input type="password" className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1 font-mono"
            placeholder="••••••••"
            value={form.razorpay_key_secret || ""}
            onChange={(e) => setForm({ ...form, razorpay_key_secret: e.target.value })}
            data-testid="razorpay-key-secret" />
        </div>
      </section>

      <section className="bg-white border border-[hsl(var(--kb-border))] p-6 space-y-4">
        <h2 className="font-display text-xl">Billing policy</h2>
        <div>
          <label className="text-sm font-medium">Downgrade policy</label>
          <select className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1"
            value={form.downgrade_policy}
            onChange={(e) => setForm({ ...form, downgrade_policy: e.target.value })}
            data-testid="downgrade-policy">
            <option value="end_of_cycle">Keep active until current cycle ends (no refund)</option>
            <option value="immediate">Cancel immediately</option>
          </select>
        </div>
        <div>
          <label className="text-sm font-medium">Default plan slug for new tenants</label>
          <input className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1 font-mono"
            value={form.default_plan_slug || ""}
            onChange={(e) => setForm({ ...form, default_plan_slug: e.target.value })}
            data-testid="default-plan-slug" />
        </div>
        <div>
          <label className="text-sm font-medium">Billing currency</label>
          <input className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1"
            value={form.billing_currency || "INR"}
            onChange={(e) => setForm({ ...form, billing_currency: e.target.value.toUpperCase() })} />
        </div>
      </section>

      <section
        className="bg-white border border-[hsl(var(--kb-border))] p-6 space-y-5"
        data-testid="platform-push-settings"
      >
        <div>
          <h2 className="font-display text-xl flex items-center gap-2">
            <Bell size={18} /> Push notification sound (global)
          </h2>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">
            Applies to every tenant&apos;s mobile push notifications. The
            selected sound is included in the FCM payload for both
            Android and iOS. Tenant admins cannot override this — it&apos;s
            a platform-wide brand decision.
          </p>
        </div>

        {/* Master switch */}
        <div>
          <label className="text-sm font-medium">Sound mode</label>
          <select
            className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1"
            value={ps.notification_sound || "default"}
            onChange={(e) => savePlatform({ notification_sound: e.target.value })}
            disabled={platformSaving || !platform}
            data-testid="platform-notification-sound"
          >
            <option value="default">Play sound (system default OR uploaded custom sound)</option>
            <option value="off">Silent — banner only, no audible alert</option>
          </select>
          <div className="text-[10px] text-[hsl(var(--kb-muted))] mt-1">
            &quot;Play sound&quot; uses the uploaded custom sound (below) if
            present, otherwise the OS default notification chime. &quot;Silent&quot;
            suppresses audio across every tenant.
          </div>
        </div>

        {/* Custom sound upload card */}
        <div
          className="border border-[hsl(var(--kb-border))] p-4 space-y-3 bg-[hsl(var(--kb-surface-alt))]"
          data-testid="platform-custom-sound-card"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Volume2 size={16} />
              <span className="text-sm font-medium">Custom notification sound</span>
            </div>
            {hasCustomSound ? (
              <span
                className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-green-100 text-green-800 border border-green-300"
                data-testid="custom-sound-status-active"
              >
                Active
              </span>
            ) : (
              <span
                className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-gray-100 text-gray-700 border border-gray-300"
                data-testid="custom-sound-status-default"
              >
                Using system default
              </span>
            )}
          </div>

          {hasCustomSound ? (
            <div className="space-y-2">
              <div className="text-xs">
                <span className="text-[hsl(var(--kb-muted))]">File:</span>{" "}
                <code className="font-mono" data-testid="custom-sound-filename">
                  {ps.notification_sound_filename}
                </code>
              </div>
              <div className="text-xs">
                <span className="text-[hsl(var(--kb-muted))]">Android resource:</span>{" "}
                <code className="font-mono">{ps.notification_sound_resource}.{ps.notification_sound_ext}</code>
                <span className="text-[10px] text-[hsl(var(--kb-muted))] ml-2">(res/raw/)</span>
              </div>
              <audio
                controls
                src={ps.notification_sound_url}
                className="w-full mt-1"
                data-testid="custom-sound-player"
              >
                Your browser does not support audio playback.
              </audio>
              <button
                onClick={removeSound}
                disabled={uploadingSound}
                className="text-xs inline-flex items-center gap-1.5 text-red-700 hover:underline disabled:opacity-50"
                data-testid="remove-custom-sound-btn"
              >
                <Trash2 size={12} /> Remove &amp; revert to system default
              </button>
            </div>
          ) : (
            <p className="text-xs text-[hsl(var(--kb-muted))]">
              No custom sound configured. Push notifications will play the
              device&apos;s system default chime.
            </p>
          )}

          <div className="pt-2 border-t border-[hsl(var(--kb-border))]">
            <label className="text-xs font-medium block mb-2">
              {hasCustomSound ? "Replace sound file" : "Upload sound file"}
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept={allowed.map((e) => `.${e}`).join(",")}
              onChange={(e) => uploadSound(e.target.files?.[0])}
              disabled={uploadingSound}
              className="block text-xs w-full file:mr-3 file:py-1.5 file:px-3 file:border-0 file:text-xs file:bg-black file:text-white hover:file:bg-gray-800 file:cursor-pointer disabled:opacity-50"
              data-testid="custom-sound-upload-input"
            />
            <div className="text-[10px] text-[hsl(var(--kb-muted))] mt-2 space-y-1">
              <div>
                Allowed: <code className="font-mono">{allowed.join(", ")}</code> &middot;
                Max size: <strong>{maxKB} KB</strong> &middot;
                Recommended duration: <strong>&lt; 30 seconds</strong>
              </div>
              <div>
                <strong>Note:</strong> After uploading, the file is included
                in the FCM payload. On Android, you must <strong>re-run{" "}
                <code className="font-mono">scripts/build_app.py</code></strong>{" "}
                so the file is baked into the APK at{" "}
                <code className="font-mono">res/raw/&lt;name&gt;.&lt;ext&gt;</code>;
                otherwise older app versions play the default. On iOS the
                file must be added to the app bundle and re-archived via
                Xcode (the build script copies it into{" "}
                <code className="font-mono">ios/App/App/</code>).
              </div>
            </div>
            {uploadingSound && (
              <div
                className="text-[10px] text-[hsl(var(--kb-muted))] mt-1 flex items-center gap-1"
                data-testid="custom-sound-uploading"
              >
                <Upload size={10} className="animate-pulse" /> Working…
              </div>
            )}
          </div>
        </div>

        {/* Icon resource */}
        <div>
          <label className="text-sm font-medium">Android notification icon resource</label>
          <input
            className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1 font-mono"
            value={ps.notification_icon || "ic_stat_notification"}
            onChange={(e) =>
              setPlatform((p) => ({
                ...(p || {}),
                settings: { ...(p?.settings || {}), notification_icon: e.target.value },
              }))
            }
            onBlur={(e) => {
              const val = e.target.value.trim();
              if (val && val !== "ic_stat_notification") {
                savePlatform({ notification_icon: val });
              }
            }}
            data-testid="platform-notification-icon"
          />
          <div className="text-[10px] text-[hsl(var(--kb-muted))] mt-1">
            Name of the drawable resource shipped in each tenant&apos;s APK
            (set by <code className="font-mono">scripts/build_app.py</code>).
            Default <code className="font-mono">ic_stat_notification</code>{" "}
            ships a white-alpha bell silhouette tinted with the storefront
            brand colour.
          </div>
        </div>
      </section>

      <button onClick={save} disabled={saving} className="kb-btn-primary inline-flex items-center gap-2" data-testid="settings-save-btn">
        <Save size={14} /> {saving ? "Saving…" : "Save settings"}
      </button>
    </div>
  );
}
